import { isFeatureEnabled } from '@/config/features';
import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { alertsApi } from '@/features/alerts/api';
import { notify } from '@/lib/notify';
import { useNotifications } from '@/contexts/NotificationContext';
import { useDesktopNotification } from '@/hooks/useDesktopNotification';
import { usePermission } from '@/hooks/usePermission';
import { apiErrorMessage } from '@/api/errors';
import type { AlertBulkAction, AlertListParams, SecurityAlert } from '@/features/alerts/types';
import { POLL_FAST, POLL_DEFAULT } from '@/lib/polling';

const keys = {
  all: ['alerts'] as const,
  list: (p: AlertListParams) => ['alerts', 'list', p] as const,
  stats: ['alerts', 'stats'] as const,
  detail: (id: string) => ['alerts', 'detail', id] as const,
  watch: ['alerts', 'watch'] as const,
};
export const useAlertList = (p: AlertListParams) =>
  useQuery({ queryKey: keys.list(p), queryFn: () => alertsApi.list(p), placeholderData: (x) => x, refetchInterval: POLL_FAST });

export const useAlertStats = () =>
  useQuery({ queryKey: keys.stats, queryFn: alertsApi.stats, refetchInterval: POLL_DEFAULT, retry: false });

export const useAlertDetail = (id: string | null) =>
  useQuery({ queryKey: keys.detail(id ?? ''), queryFn: () => alertsApi.detail(id!), enabled: !!id });

function useAlertAction(fn: (id: string) => Promise<void>, success: string, fail: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); notify.success(success); },
    onError: (e) => notify.error(apiErrorMessage(e, fail)),
  });
}

export const useAcknowledgeAlert = () => useAlertAction((id) => alertsApi.acknowledge(id), 'Alert acknowledged', 'Could not acknowledge');
export const useResolveAlert = () => useAlertAction((id) => alertsApi.resolve(id), 'Alert resolved', 'Could not resolve');
export const useEscalateAlert = () => useAlertAction((id) => alertsApi.escalate(id), 'Alert escalated', 'Could not escalate');

export function useAssignAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, assigneeId }: { id: string; assigneeId: string }) => alertsApi.assign(id, assigneeId),
    onSuccess: (_d, v) => { qc.invalidateQueries({ queryKey: keys.all }); qc.invalidateQueries({ queryKey: keys.detail(v.id) }); notify.success('Owner assigned'); },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not assign owner')),
  });
}

export function useAddAlertNote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => alertsApi.addNote(id, note),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.detail(id) }); notify.success('Note added'); },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not add note')),
  });
}

export function useBulkAlertAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, ids }: { action: AlertBulkAction; ids: string[] }) => alertsApi.bulk(action, ids),
    onSuccess: ({ affected, failed }, { action }) => {
      qc.invalidateQueries({ queryKey: keys.all });
      const verb = action === 'acknowledge' ? 'acknowledged' : action === 'resolve' ? 'resolved' : 'escalated';
      if (!failed) notify.success(`${affected} alert${affected === 1 ? '' : 's'} ${verb}`);
      else notify.error(`${affected} ${verb}, ${failed} failed`);
    },
    onError: (e) => notify.error(apiErrorMessage(e, 'Bulk action failed')),
  });
}

/**
 * Live security-alert watcher. Runs anywhere it's mounted (the always-present
 * NotificationBell). For security admins only, it polls active alerts and, for
 * NEW ones, pushes to the in-app notification centre (unread counter + toast)
 * and raises a desktop notification. This is the immediate-notification channel
 * required by the sprint, reusing the existing notification infrastructure. It
 * uses polling (works with the in-memory bearer auth); the backend also exposes
 * an SSE stream as the upgrade path (see PROJECT_STATE real-time note).
 */
export function useSecurityAlertsWatcher() {
  const { can } = usePermission();
  // Dormant until the security-alert backend is deployed (config/features.ts),
  // so the shell never polls an unavailable API.
  const enabled = isFeatureEnabled('security-alerts') && (can('security.view') || can('security.manage'));
  const { push } = useNotifications();
  const { notify: desktop } = useDesktopNotification();
  const navigate = useNavigate();
  const seen = useRef<Set<string>>(new Set());
  const primed = useRef(false);

  const { data } = useQuery({
    queryKey: keys.watch,
    queryFn: () => alertsApi.list({ page: 1, pageSize: 20, status: 'active' }),
    enabled,
    refetchInterval: POLL_FAST,
    retry: false,
  });

  useEffect(() => {
    if (!data) return;
    const items = data.data;
    // First load only records the baseline — don't notify for pre-existing alerts.
    if (!primed.current) { items.forEach((a) => seen.current.add(a.id)); primed.current = true; return; }
    const fresh = items.filter((a) => !seen.current.has(a.id));
    fresh.forEach((a: SecurityAlert) => {
      seen.current.add(a.id);
      const level = a.severity === 'critical' || a.severity === 'high' ? 'error' : 'warning';
      push({ level, title: a.title, message: a.description, href: '/security-alerts', toast: true });
      if (a.severity === 'critical') {
        desktop(`Critical: ${a.title}`, { body: a.description, onClick: () => navigate('/security-alerts') });
      }
    });
  }, [data, push, desktop, navigate]);
}
